
import { Megaphone, ChevronRight, Calendar, Search, Zap } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useNotices, Notice } from '@/shared/hooks/useNotices';
import { useNavigate } from 'react-router-dom';

const NoticesPage: React.FC = () => {
    const { notices, loading: noticesLoading } = useNotices();
    const navigate = useNavigate();

    const getCategoryStyle = (cat: string) => {
        switch (cat) {
            case '공지':
            case 'Notice': return 'text-blue-500 bg-blue-500/10';
            case 'Listing': return 'text-emerald-500 bg-emerald-500/10';
            case 'Security': return 'text-red-500 bg-red-500/10';
            default: return 'text-slate-500 bg-slate-500/10';
        }
    };

    if (noticesLoading) {
        return <div className="min-h-screen flex items-center justify-center">
            <Zap className="h-12 w-12 text-blue-500 animate-spin" />
        </div>;
    }

    return (
        <div className="min-h-screen pb-24 text-foreground animate-in fade-in duration-1000">
            <div className="max-w-5xl mx-auto px-4 lg:px-8 space-y-12">

                {/* Header Section */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pt-8">
                    <div className="space-y-4 max-w-2xl text-left">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-500 transition-colors">
                            <Megaphone className="h-3.5 w-3.5 fill-blue-500" />
                            <span className="text-[10px] font-black uppercase tracking-[0.2em]">최신 업데이트 및 프로토콜 소식</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.9] uppercase">
                            중요 공지 및<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-500 dark:via-indigo-500 dark:to-purple-500">
                                시스템 업데이트
                            </span>
                        </h1>
                        <p className="text-muted-foreground font-bold text-sm md:text-base leading-relaxed max-w-lg uppercase tracking-tight">
                            플랫폼의 최신 소식과 서비스 안정성을 위한 중요 업데이트를<br />
                            가장 먼저 확인하고 안전한 자산 관리를 유지하세요.
                        </p>
                    </div>

                    <div className="relative group min-w-[320px]">
                        <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50 group-focus-within:text-blue-500 transition-colors" />
                        <input
                            className="h-14 w-full bg-card border border-border rounded-2xl pl-14 pr-6 text-xs font-black outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/5 transition-all placeholder:text-muted-foreground/30 uppercase tracking-widest"
                            placeholder="제목 또는 카테고리로 검색..."
                        />
                    </div>
                </div>

                {/* Notices List */}
                <div className="grid gap-4">
                    {notices.map((notice: Notice) => (
                        <div
                            key={notice.id}
                            onClick={() => navigate(`/notices/${notice.id}`)}
                            className="group relative p-8 rounded-[2.5rem] bg-card border border-border hover:border-blue-500/30 transition-all duration-500 cursor-pointer overflow-hidden shadow-xl hover:shadow-2xl translate-y-0 hover:-translate-y-1 active:scale-[0.99]"
                        >
                            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/[0.02] blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>

                            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                                <div className="flex items-center gap-8 flex-1 min-w-0">
                                    <div className={cn(
                                        "shrink-0 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] border transition-colors",
                                        getCategoryStyle(notice.category)
                                    )}>
                                        {notice.category}
                                    </div>
                                    <div className="space-y-2 min-w-0">
                                        <div className="flex items-center gap-3">
                                            <h3 className="text-lg md:text-xl font-black text-foreground group-hover:text-blue-600 transition-colors truncate tracking-tight">
                                                {notice.title}
                                            </h3>
                                            {notice.isHot && (
                                                <span className="flex-shrink-0 px-2 py-0.5 rounded-md bg-rose-500/10 text-rose-500 text-[8px] font-black uppercase tracking-widest animate-pulse border border-rose-500/20">
                                                    HOT
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-4 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] italic opacity-60">
                                            <span>공식 프로토콜 공지사항</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="shrink-0 flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-accent border border-border flex items-center justify-center text-muted-foreground group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-500/50 transition-all duration-500">
                                        <ChevronRight className="h-6 w-6 group-hover:translate-x-0.5 transition-transform" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {notices.length === 0 && (
                        <div className="text-center py-20 opacity-30">
                            <p className="text-xs font-black uppercase tracking-widest">현재 등록된 공지사항이 없습니다.</p>
                        </div>
                    )}

                    <div className="mt-12 flex justify-center">
                        <button className="group relative px-10 py-5 rounded-2xl bg-accent border border-border text-[11px] font-black text-muted-foreground uppercase tracking-[0.3em] italic hover:bg-foreground hover:text-background hover:border-foreground transition-all duration-300">
                            이전 공지사항 더보기
                            <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl"></div>
                        </button>
                    </div>
                </div>

                {/* Footer Disclaimer */}
                <div className="pt-20 border-t border-border opacity-30 text-center">
                    <p className="text-[9px] font-black uppercase tracking-[0.5em]">Global Communications Protocol v2.4.0</p>
                </div>
            </div>
        </div>
    );
};

export default NoticesPage;
